<template>
    <div>
        <div class="pb-10 mb-12 border-b border-gray-200">
            <h2 class="pb-1 text-2xl font-bold text-left text-black no-underline normal-case non-italic">Complete Your Profile</h2>
            <p class="text-lg font-normal text-left no-underline normal-case non-italic">Kindly fill out the form. Due to regulatory compliance, all fields are required.</p>
        </div>

        <app-errors :form="form" class="mb-10"></app-errors>

        <!-- PRIMARY -->
        <personal-information-form v-model="form.primary"></personal-information-form>
        <!-- PRIMARY -->

        <!-- INVESTING TYPE -->
        <div class="mt-12">
            <h3 class="pb-4 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">I plan to invest as/through:</h3>

            <div class="space-y-4">
                <radio-field v-model="form.investingType" value="individual">An Individual</radio-field>
                <div>
                    <radio-field v-model="form.investingType" value="joint">Joint with Spouse</radio-field>
                    <div v-show="form.investingType == 'joint'" class="mt-4 space-y-4 ml-7">
                        <radio-field v-model="form.subInvestingType" value="joint-tenants-survivorship">Joint Tenants with Rights of Survivorship (most common)</radio-field>
                        <radio-field v-model="form.subInvestingType" value="joint-tenants-common">Tenants in Common</radio-field>
                        <radio-field v-model="form.subInvestingType" value="joint-tenants-entirety">Tenants by Entirety</radio-field>
                    </div>
                </div>
                <radio-field v-model="form.investingType" value="entity">Entity or Trust</radio-field>
                <div v-if="series != 'nsight'">
                    <radio-field v-model="form.investingType" value="ira">IRA</radio-field>
                    <div v-show="form.investingType == 'ira'" class="mt-4 space-y-4 ml-7">
                        <radio-field v-model="form.subInvestingType" value="ira-traditional">Traditional IRA</radio-field>
                        <radio-field v-model="form.subInvestingType" value="ira-roth">Roth IRA</radio-field>
                    </div>
                </div>
                <div>
                    <radio-field v-model="form.investingType" value="other">Something Else</radio-field>
                    <div v-show="form.investingType == 'other'" class="mt-4 ml-7">
                        <textarea-field v-model="form.subInvestingType" label="Please describe how you wish to invest" :required="true"></textarea-field>
                    </div>
                </div>
            </div>
        </div><!-- INVESTING TYPE -->

        <!-- SECONDARY -->
        <personal-information-form v-if="form.investingType == 'joint'" v-model="form.secondary" title="Spouse Personal Information" :showEmail="true" :isSpouse="true" @replicate="copyAsAbove" class="mt-12 border-t border-gray-200 pt-11"></personal-information-form>
        <!-- SECONDARY -->

        <!-- ENTITY -->
        <entity-information-form v-if="form.investingType == 'entity'" v-model="form.entity" @replicate="copyAsAbove" :series="series"></entity-information-form>

        <!-- IRA -->
        <ira-information-form v-if="form.investingType == 'ira'" v-model="form.ira" :is-investor-married="form.primary.maritalStatus"></ira-information-form>

        <div v-if="!kycDocumentsForm.showProgress" class="mt-20 sm:flex sm:justify-end">
            <div class="inline-flex items-center">
                <div class="sm:ml-3">
                    <app-button @click="handleNext" :busy="form.busy">Save and Continue</app-button>
                </div>
            </div>
        </div>
    </div>

    <teleport to="body">
        <div v-show="kycDocumentsForm.showForm && !kycQuestionsForm.showForm" class="fixed inset-0 overflow-y-auto" style="z-index: 999999;">
            <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-all transform pointer-events-none">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform sm:my-8 sm:align-middle sm:w-full sm:p-6" style="max-width: 780px; max-height: 90%;">
                    <div class="rounded-md shadow">
                        <div class="relative p-10 bg-white rounded-md">
                            <div v-if="!kycDocumentsForm.showProgress" class="absolute top-0 right-0 block pt-4 pr-4">
                                <app-button @click="kycDocumentsForm.showForm = false; kycDocumentsForm.showProgress = false" class="!p-0 !bg-white"><svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg></app-button>
                            </div>

                            <div v-if="kycDocumentsForm.showProgress" class="text-center">
                                <h2 class="pb-1 text-xl font-bold text-center text-black no-underline normal-case non-italic">Verification In Progress</h2>
                                <p class="font-normal text-center no-underline normal-case text-md non-italic">Please check back later for updates.</p>

                                <div class="flex items-center justify-center space-x-6">
                                    <a href="#" @click.prevent="handleReturn" class="inline-block mt-6 text-sm font-bold text-black border-b-2 border-teal">Return &rarr;</a>
                                    <a href="#" @click.prevent="$emit('next', true)" class="inline-block mt-6 text-sm font-bold text-black border-b-2 border-teal">Skip &rarr;</a>
                                </div>
                            </div>

                            <div v-else>
                                <div class="pb-6 mb-6 border-b border-gray-200">
                                    <h2 class="pb-1 text-xl font-bold text-left text-black no-underline normal-case non-italic">Investor ID Verification</h2>
                                    <p class="font-normal text-left no-underline normal-case text-md non-italic">Please upload a picture of your government ID (Example: Passport, Driver’s License, State ID, or other Govt. ID)</p>
                                </div>

                                <div class="space-y-8">
                                    <app-errors :form="kycDocumentsForm" class="mb-10"></app-errors>

                                    <uploads-field v-model="kycDocumentsForm.primaryDocuments" desc="Accepts JPG, PNG and PDF files up to 10MB" accept=".pdf,.jpg,.png" :disabled="kycDocumentsForm.busy"></uploads-field>

                                    <uploads-field v-if="form.investingType == 'joint'" v-model="kycDocumentsForm.secondaryDocuments" label="Upload spouse documents for verification" desc="Accepts JPG, PNG and PDF files up to 10MB" accept=".pdf,.jpg,.png" :disabled="kycDocumentsForm.busy"></uploads-field>

                                    <uploads-field v-if="form.investingType == 'entity'" v-model="kycDocumentsForm.entityDocuments" label="Upload entity documents for verification" desc="Accepts JPG, PNG and PDF files up to 10MB" accept=".pdf,.jpg,.png" :disabled="kycDocumentsForm.busy"></uploads-field>

                                    <div class="flex flex-col items-center justify-center max-w-md mx-auto space-y-4">
                                        <app-button @click="handleUploadKycDocuments" :busy="kycDocumentsForm.busy" class="!w-full">Send for Verification</app-button>

                                        <app-button @click="handleSendVerification" class="!w-full !border-gray-300 !bg-white !text-black">Email me and I’ll complete this later</app-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>

    <teleport to="body">
        <div v-show="kycQuestionsForm.showForm" class="fixed inset-0 overflow-y-auto" style="z-index: 999999;">
            <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-all transform pointer-events-none">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div class="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform sm:my-8 sm:align-middle sm:w-full sm:p-6" style="max-width: 840px; max-height: 90%;">
                    <div class="rounded-md shadow">
                        <div class="relative p-10 space-y-10 bg-white rounded-md">
                            <div v-show="form.primary.kycQuestions.length > 0">
                                <form @submit.prevent>
                                    <h3 class="block mb-3 text-lg font-bold text-gray-700">Your KYC Questionnaire</h3>
                                    <div class="p-5 space-y-8 bg-gray-100 rounded-md">
                                        <div v-for="(question, qindex) in form.primary.kycQuestions" :key="qindex">
                                            <label class="block text-sm font-bold text-gray-700">{{ question.prompt }}</label>
                                            <div class="mt-3 space-y-4">
                                                <template v-for="(answer, aindex) in question.answer" :key="aindex">
                                                    <radio-field :name="question.type" @input="handleAnswerKycQuestion('primary', qindex, question.type, answer)">{{ answer }}</radio-field>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div v-show="form.secondary.kycQuestions.length > 0">
                                <form @submit.prevent>
                                    <h3 class="block mb-3 text-lg font-bold text-gray-700">Spouse KYC Questionnaire</h3>
                                    <div class="p-5 space-y-8 bg-gray-100 rounded-md">
                                        <div v-for="(question, qindex) in form.secondary.kycQuestions" :key="qindex">
                                            <label class="block text-sm font-bold text-gray-700">{{ question.prompt }}</label>
                                            <div class="mt-3 space-y-4">
                                                <template v-for="(answer, aindex) in question.answer" :key="aindex">
                                                    <radio-field :name="question.type" :value="answer">{{ answer }}</radio-field>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div v-show="form.entity.kycQuestions.length > 0">
                                <form @submit.prevent>
                                    <h3 class="block mb-3 text-lg font-bold text-gray-700">Entity KYC Questionnaire</h3>
                                    <div class="p-5 space-y-8 bg-gray-100 rounded-md">
                                        <div v-for="(question, qindex) in form.entity.kycQuestions" :key="qindex">
                                            <label class="block text-sm font-bold text-gray-700">{{ question.prompt }}</label>
                                            <div class="mt-3 space-y-4">
                                                <template v-for="(answer, aindex) in question.answer" :key="aindex">
                                                    <radio-field :name="question.type" :value="answer">{{ answer }}</radio-field>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div class="flex flex-col items-center justify-center max-w-md mx-auto space-y-4">
                                <app-button @click="handleKycQuestions" :busy="kycQuestionsForm.busy" class="!w-full">Submit</app-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import AppButton from '@Components/Button'
import AppErrors from '@Components/Errors'
import TextField from '@Fields/Text'
import TextareaField from '@Fields/Textarea'
import SelectField from '@Fields/Select'
import CheckboxField from '@Fields/Checkbox'
import RadioField from '@Fields/Radio'
import DateField from '@Fields/Date'
import UploadsField from '@Fields/Uploads'
import PersonalInformationForm from '@/Pages/Intake/Parts/PersonalInformation'
import IraInformationForm from '@/Pages/Intake/Parts/IraInformation'
import EntityInformationForm from '@/Pages/Intake/Parts/EntityInformation'
import useForm from '@/form'
import { useStore } from 'vuex'
import { onMounted, watch } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'

const defKycQuestionFields = () => {
    const fields = []

    for(var i = 1; i <= 5; i++){
        var field = {}

        field['type'] = ''
        field['qns'] = ''

        fields.push(field)
    }

    return fields
}

export default {
    components: {
        AppButton,
        AppErrors,
        TextField,
        TextareaField,
        SelectField,
        CheckboxField,
        RadioField,
        DateField,
        UploadsField,
        PersonalInformationForm,
        IraInformationForm,
        EntityInformationForm
    },
    props: ['series'],
    emits: ['prev', 'next', 'process', 'complete', 'goto'],
    setup(props, { emit }){
        const pageProps = usePage().props.value

        const store = useStore()

        const form = useForm(_.merge({
            investingType: 'individual',
            subInvestingType: '',
            accountId: '',
            primary: {
                salutation: '',
                firstName: (_.has(pageProps, 'auth.user.first_name')) ? pageProps.auth.user.first_name : '',
                lastName: (_.has(pageProps, 'auth.user.last_name')) ? pageProps.auth.user.last_name : '',
                middleInitial: '',
                suffix: '',
                nickname: '',
                maritalStatus: '',
                mobilePhone: '',
                phone: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
                employment: '',
                occupation: '',
                employer: '',
                empOther: '',
                citizenship: 'U.S. Citizen',
                dob: '',
                idType: '',
                idNumber: '',
                idExpiration: '',
                idIssuer: '',
                ssn: '',
                email: (_.has(pageProps, 'auth.user.email')) ? pageProps.auth.user.email : '',
                financialAffiliation: 'No',
                financialFirm: '',
                financialFirmPosition: '',
                publicCompanyAffiliation: 'No',
                publicCompany: '',
                publicCompanyPosition: '',
                politicalAffiliation: 'No',
                politicalCountry: '',
                politicalRole: '',
                partyId: '',
                kycId: '',
                kycStatus: '',
                kycQuestions: [],
                kycDocuments: []
            },
            secondary: {
                salutation: '',
                firstName: '',
                lastName: '',
                middleInitial: '',
                suffix: '',
                nickname: '',
                maritalStatus: '',
                mobilePhone: '',
                phone: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
                employment: '',
                occupation: '',
                employer: '',
                empOther: '',
                citizenship: 'U.S. Citizen',
                dob: '',
                idType: '',
                idNumber: '',
                idExpiration: '',
                idIssuer: '',
                ssn: '',
                email: '',
                financialAffiliation: 'No',
                financialFirm: '',
                financialFirmPosition: '',
                publicCompanyAffiliation: 'No',
                publicCompany: '',
                publicCompanyPosition: '',
                politicalAffiliation: 'No',
                politicalCountry: '',
                politicalRole: '',
                partyId: '',
                kycId: '',
                kycStatus: '',
                kycQuestions: [],
                kycDocuments: []
            },
            entity: {
                entityName: '',
                taxId: '',
                entityType: '',
                soleSignature: 'No',
                moreSignatures: 'No',
                role: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
                else25shares: 'No',
                partyId: '',
                kycId: '',
                kycStatus: '',
                kycQuestions: [],
                kycDocuments: [],
                beneficiary: [{
                    firstName: '',
                    lastName: '',
                    nickname: '',
                    address: '',
                    ssn: '',
                    dob: '',
                    idNumber: ''
                }]
            },
            ira: {
                hasAccount: '',
                custodianName: '',
                accountNumber: '',
                accountName: '',
                address1: '',
                address2: '',
                phone: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
                ccName: '',
                ccNumber: '',
                ccExpiration: '',
                ccVerificationCode: '',
                beneficiary: [{
                    firstName: '',
                    lastName: '',
                    nickname: '',
                    ssn: '',
                    relation: '',
                    dob: '',
                    address: '',
                    percentage: 100
                }]
            },
            other: {
                description: ''
            }
        }, store.state.profileForm))

        const kycQuestionsForm = useForm({
            showForm: false,
            investingType: '',
            primary: defKycQuestionFields(),
            secondary: defKycQuestionFields(),
            entity: defKycQuestionFields()
        })

        const kycDocumentsForm = useForm({
            showForm: false,
            showProgress: false,
            investingType: '',
            primaryDocuments: [],
            secondaryDocuments: [],
            entityDocuments: []
        })

        watch(() => form.investingType, (state) => {
            form.reset()
            form.subInvestingType = ''
        });

        onMounted(async () => {
            window.scroll(0, 0)

            if(showKycQuestions(form.data())) {
                kycQuestionsForm.showForm = true
            }

            if(showKycForm(form.data())) {
                kycDocumentsForm.showForm = true
                kycDocumentsForm.primaryDocuments = form.primary.kycDocuments
                kycDocumentsForm.secondaryDocuments =  form.secondary.kycDocuments
                kycDocumentsForm.entityDocuments = form.entity.kycDocuments
            }

            if (showKycProgress(form.data())) {
                kycDocumentsForm.showProgress = false
            }
        })

        function showKycQuestions(data){
            if (_.get(data, 'primary.kycStatus') == 'FAIL') {
                //return false
            }

            if (
                (_.get(data, 'primary.kycQuestions', [])).length > 0 ||
                (_.get(data, 'secondary.kycQuestions', [])).length > 0 ||
                (_.get(data, 'entity.kycQuestions', [])).length > 0
            ) {
                return true
            }

            return false
        }

        function showKycForm(data){
            var kycStatus = _.get(data, 'primary.kycStatus')
            var kycId = _.get(data, 'primary.kycId')
            var showKyc = (kycStatus == 'FAIL' || (kycStatus == '' && kycId != '')) ? true : false

            if (_.get(data, 'investingType') == 'joint' && _.get(data, 'secondary.kycStatus') == 'FAIL') {
                showKyc = true
            }

            if (_.get(data, 'investingType') == 'entity' && _.get(data, 'entity.kycStatus') == 'FAIL') {
                showKyc = true
            }

            return showKyc
        }

        function showKycProgress(data){
            var show = (!_.isEmpty(_.get(data, 'primary.kycDocuments'))) ? true : false

            if (_.get(data, 'investingType') == 'joint' && _.isEmpty(_.get(data, 'secondary.kycDocuments'))) {
                show = false
            }

            if (_.get(data, 'investingType') == 'entity' && _.isEmpty(_.get(data, 'entity.kycDocuments'))) {
                show = false
            }

            return (showKycForm(data) && show) ? true : false
        }

        function copyAsAbove(type){
            const keys = ['address1', 'address2', 'city', 'state', 'zip', 'country', 'citizenship']

            _.each(keys, (key) => {
                if (_.has(form[type], key)) {
                    form[type][key] = form.primary[key]
                }
            })
        }

        function handleNext(){
            form.post(route('intake.validate.profile')).then(response => {
                if (response.status == 200) {
                    store.commit('UPDATE_FORM_DATA', {
                        form: 'profileForm',
                        data: response.data
                    });

                    if (showKycQuestions(response.data)) {
                        kycQuestionsForm.showForm = true
                        form.primary.kycQuestions = _.get(response.data, 'primary.kycQuestions', [])
                        form.secondary.kycQuestions = _.get(response.data, 'secondary.kycQuestions', [])
                        form.entity.kycQuestions = _.get(response.data, 'entity.kycQuestions', [])

                        return false
                    }

                    if (showKycForm(response.data)) {
                        kycDocumentsForm.showForm = true

                        return false
                    }

                    emit('next', true)
                }
            }).catch(errors => {
                window.scroll(0,0)
                console.log(errors.response)
            })
        }

        function handleUploadKycDocuments(){
            kycDocumentsForm.investingType = form.investingType

            kycDocumentsForm.post(route('intake.upload.kyc')).then(response => {
                if (response.status == 200) {
                    emit('next', true)
                }
            }).catch(error => {
                console.log(error.response)
            })
        }

        function handleSendVerification(){
            //axios.post(route('intake.email.investor-verification'))

            emit('next', true)
        }

        function handleAnswerKycQuestion(target, index, key, value){
            kycQuestionsForm[target][index]['type'] = key
            kycQuestionsForm[target][index]['qns'] = value
        }

        function handleKycQuestions(){
            kycQuestionsForm.investingType = form.investingType

            kycQuestionsForm.post(route('intake.update.kyc')).then(response => {
                console.log(response.data)
                if (response.status == 200 && response.data == 'PASS') {
                    emit('next', true)
                } else {
                    window.location.reload()
                }
            }).catch(error => {
                console.log(error.response)
            })
        }

        function handleReturn(){
            kycDocumentsForm.showForm = false
            kycDocumentsForm.showProgress = false
        }

        return {
            form,
            kycQuestionsForm,
            kycDocumentsForm,
            copyAsAbove,
            handleNext,
            handleUploadKycDocuments,
            handleSendVerification,
            handleAnswerKycQuestion,
            handleKycQuestions,
            handleReturn
        };
    },
}
</script>

<style>

</style>
